/* ------------------------------------------------------------------
   ProfileKitCard.js  —  'Your AI profile kit' in Link in Bio (Growth round, T23)
   The kit made on a name-generator page before sign-up is attached to the
   account at sign-up (backend/services/profileKits.js). This card shows
   the whole of it: every bio (Copy / Use as my bio), the captions for the
   first 3 posts and the hashtag set. Hidden when the user has no kit.
------------------------------------------------------------------ */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const ax = axios.create({ withCredentials: true });
const AI = '#4f46e5';

export default function ProfileKitCard({ onUseBio }) {
  const [kit, setKit] = useState(null);
  const [copied, setCopied] = useState('');
  const timer = useRef(null);

  useEffect(() => {
    let live = true;
    ax.get('/api/ai/profile-kit')
      .then(({ data }) => { if (live && data && data.bio && Array.isArray(data.captions)) setKit(data); })
      .catch(() => { /* 404: no kit yet */ });
    return () => { live = false; clearTimeout(timer.current); };
  }, []);

  if (!kit) return null;

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(''), 1400);
    } catch { /* clipboard blocked */ }
  };
  const bios = kit.bio.variants || [kit.bio.text];
  const Copy = ({ text, label }) => (
    <button type="button" onClick={() => copy(text, label)} style={ghostBtn}>{copied === label ? 'Copied' : 'Copy'}</button>
  );

  return (
    <div data-testid="profile-kit-card" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill={AI} aria-hidden="true"><path d="M12 2l1.8 5.2L19 9l-5.2 1.8L12 16l-1.8-5.2L5 9l5.2-1.8zM19 14l.9 2.6 2.6.9-2.6.9L19 21l-.9-2.6-2.6-.9 2.6-.9z" /></svg>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Your AI profile kit for @{kit.name}</div>
      </div>
      <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 14 }}>Made for you on the name generator. It's yours to keep.</div>

      <Section title={`Bios · ${bios.length}`}>
        {bios.map((b, i) => (
          <Row key={i}>
            <div style={{ flex: 1, fontSize: 14, color: '#111827', whiteSpace: 'pre-line' }}>{b}</div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              {onUseBio && <button type="button" onClick={() => onUseBio(b)} style={{ ...ghostBtn, color: AI, borderColor: '#c7d2fe' }}>Use as my bio</button>}
              <Copy text={b} label={`bio-${i}`} />
            </div>
          </Row>
        ))}
      </Section>

      <Section title="Your first 3 posts · captions">
        {kit.captions.map((c, i) => (
          <Row key={i}>
            <div style={{ flex: 1, fontSize: 14, color: '#111827', whiteSpace: 'pre-line' }}><strong>Post {i + 1}:</strong> {c}</div>
            <Copy text={c} label={`caption-${i}`} />
          </Row>
        ))}
      </Section>

      <Section title={`Hashtag set · ${kit.hashtags.length}`} last>
        <Row>
          <div style={{ flex: 1, fontSize: 14, color: '#374151' }}>{kit.hashtags.join(' ')}</div>
          <Copy text={kit.hashtags.join(' ')} label="hashtags" />
        </Row>
      </Section>
    </div>
  );
}

const ghostBtn = { padding: '6px 12px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const Section = ({ title, last, children }) => (
  <div style={{ marginBottom: last ? 0 : 14 }}>
    <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>{title}</div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{children}</div>
  </div>
);
const Row = ({ children }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 12px' }}>{children}</div>
);
